"use client";

import { useMemo } from "react";
import { STANDARD_EMOJIS, searchStandardEmojis, type EmojiCategory, type StandardEmoji } from "@/lib/standard-emojis";
import type { CustomEmoji } from "@/lib/api/emojis";
import { Emoji } from "@/components/ui/Emoji";

const CATEGORY_LABELS: Record<EmojiCategory, string> = {
  smileys: "Smileys",
  people: "Mennesker",
  nature: "Natur",
  food: "Mad & drikke",
  activities: "Aktiviteter",
  travel: "Rejser & steder",
  objects: "Objekter",
  symbols: "Symboler",
  flags: "Flag",
};

/** Group standard emojis by category, preserving the source list's order. */
function groupByCategory(list: StandardEmoji[]): Array<[EmojiCategory, StandardEmoji[]]> {
  const groups = new Map<EmojiCategory, StandardEmoji[]>();
  for (const emoji of list) {
    const bucket = groups.get(emoji.category);
    if (bucket) bucket.push(emoji);
    else groups.set(emoji.category, [emoji]);
  }
  return Array.from(groups.entries());
}

interface EmojiGridProps {
  /**
   * Custom emoji to show, already resolved by the caller — one group's for
   * composing text, the post's own group's or every group DM participants
   * share (already combined into one flat list) for reacting. This
   * component doesn't know or care where they came from.
   */
  customEmojis: CustomEmoji[];
  search: string;
  onSelectStandard: (emoji: StandardEmoji) => void;
  onSelectCustom: (emoji: CustomEmoji) => void;
  /** Section label above the custom-emoji grid. */
  customSectionLabel?: string;
  /** Shown in place of the custom-emoji grid when there are none browsing
   * (not searching) and this is provided — omit to skip the section
   * entirely when there's nothing to show. */
  customEmptyMessage?: string;
  /** Grid density. 8 (default) suits the full-width composer picker; 6 suits
   * a narrower popover (e.g. EmojiReactionPicker's chat-dock-sized menu). */
  columns?: 6 | 8;
}

/**
 * The shared search-results / category-browsing grid behind both
 * EmojiPicker (composing text) and EmojiReactionPicker (reacting) — search
 * box and any "frequently used" row are each caller's own, this is just the
 * scrollable results area.
 */
export function EmojiGrid({
  customEmojis,
  search,
  onSelectStandard,
  onSelectCustom,
  customSectionLabel = "Gruppens emojis",
  customEmptyMessage,
  columns = 8,
}: EmojiGridProps) {
  const filtered = search
    ? customEmojis.filter((e) => e.shortcode.includes(search.toLowerCase()))
    : customEmojis;

  // Standard set is curated (~390 entries, not the full unicode range), so
  // rendering it all at once is cheap — no virtualization needed. Grouped by
  // category when browsing; a flat relevance-ranked list when searching.
  const standardGroups = useMemo(
    () => (search ? [] : groupByCategory(STANDARD_EMOJIS)),
    [search]
  );
  const standardFiltered = useMemo(
    () => (search ? searchStandardEmojis(search, 60) : []),
    [search]
  );

  if (search) {
    if (filtered.length === 0 && standardFiltered.length === 0) {
      return <p className="text-center text-xs text-subtle-foreground py-4">Ingen resultater</p>;
    }
    return (
      <>
        {filtered.length > 0 && (
          <EmojiSection label={customSectionLabel} columns={columns}>
            {filtered.map((emoji) => (
              <CustomEmojiButton key={emoji.id} emoji={emoji} onPick={() => onSelectCustom(emoji)} />
            ))}
          </EmojiSection>
        )}
        {standardFiltered.length > 0 && (
          <EmojiSection label="Standard" columns={columns}>
            {standardFiltered.map((emoji) => (
              <StandardEmojiButton key={emoji.shortcode} emoji={emoji} onPick={() => onSelectStandard(emoji)} />
            ))}
          </EmojiSection>
        )}
      </>
    );
  }

  return (
    <>
      {(customEmojis.length > 0 || customEmptyMessage) && (
        <EmojiSection label={customSectionLabel} columns={columns}>
          {customEmojis.length === 0 ? (
            <p className="col-span-full text-center text-xs text-subtle-foreground py-3">{customEmptyMessage}</p>
          ) : (
            customEmojis.map((emoji) => (
              <CustomEmojiButton key={emoji.id} emoji={emoji} onPick={() => onSelectCustom(emoji)} />
            ))
          )}
        </EmojiSection>
      )}

      {standardGroups.map(([category, list]) => (
        <EmojiSection key={category} label={CATEGORY_LABELS[category]} columns={columns}>
          {list.map((emoji) => (
            <StandardEmojiButton key={emoji.shortcode} emoji={emoji} onPick={() => onSelectStandard(emoji)} />
          ))}
        </EmojiSection>
      ))}
    </>
  );
}

/** A labeled group of emoji buttons in the grid. */
function EmojiSection({
  label,
  columns = 8,
  children,
}: {
  label: string;
  columns?: 6 | 8;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-2">
      <p className="px-1 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wide text-subtle-foreground">
        {label}
      </p>
      <div className={`grid gap-1 ${columns === 6 ? "grid-cols-6" : "grid-cols-8"}`}>{children}</div>
    </div>
  );
}

function CustomEmojiButton({
  emoji,
  onPick,
}: {
  /** `groupName` is set only in the DM reaction picker's merged multi-group
   * list — it's how a viewer confirms which shared group an emoji actually
   * belongs to instead of just trusting the generic "Gruppeemojis" label. */
  emoji: { shortcode: string; url: string; groupName?: string };
  onPick: () => void;
}) {
  return (
    // Named group: the plain, unnamed "group" utility matches ANY ancestor
    // .group being hovered, not just this button's own wrapper — inside a DM
    // message bubble (MessageBubble.tsx wraps the whole row in an unnamed
    // "group" for its own reply/react/report reveal-on-hover), that meant
    // hovering anywhere in the bubble lit up every emoji's tooltip at once.
    // A private namespace makes this button react only to itself.
    <div className="relative group/emoji">
      <button
        type="button"
        onClick={onPick}
        className="w-full aspect-square rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-center transition-colors"
      >
        <Emoji emoji={emoji.shortcode} url={emoji.url} size="xl" loading="lazy" />
      </button>
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1.5 bg-gray-900 dark:bg-gray-700 rounded-lg shadow-lg pointer-events-none opacity-0 group-hover/emoji:opacity-100 transition-opacity z-10 flex flex-col items-center gap-1 whitespace-nowrap">
        <Emoji emoji={emoji.shortcode} url={emoji.url} size="xl" label="" />
        <span className="text-[10px] text-white font-medium">:{emoji.shortcode}:</span>
        {emoji.groupName && (
          <span className="text-[9px] text-gray-300">{emoji.groupName}</span>
        )}
      </div>
    </div>
  );
}

function StandardEmojiButton({ emoji, onPick }: { emoji: StandardEmoji; onPick: () => void }) {
  return (
    <button
      type="button"
      onClick={onPick}
      title={`:${emoji.shortcode}:`}
      className="w-full aspect-square rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
    >
      <Emoji emoji={emoji.char} size="xl" />
    </button>
  );
}
